import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  Fade,
  Backdrop,
  Chip,
  Autocomplete,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  useAddTeam,
  useUpdateTeam,
  useUsers,
  useTeams,
  usedeleteWholeTeam,
} from "@/hooks/useTenantDashboardService";
import { useQueryClient } from "@tanstack/react-query";
import useAuthStore from "@/store/useAuthStore";
import type { AxiosError } from "axios";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface TeamMember {
  id: string;
  email: string;
  name: string;
  password: string;
  role: string;
  isVerified: boolean;
  userImg: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  plan: string;
  planMasterId: string | null;
  status: string;
  phoneNumber: string | null;
}

interface TeamData {
  id?: string;
  name?: string;
  tenantId?: string;
  createdAt?: string;
  memberCount?: number;
  members?: TeamMember[];
  status?: string;
  role?: string;
  companyName?: string;
  phoneNumber?: string;
}

interface AddTeamModalProps {
  open: boolean;
  onClose: () => void;
  editUserData?: TeamData | null;
}

const Add_Team: React.FC<AddTeamModalProps> = ({
  open,
  onClose,
  editUserData,
}) => {
  const { isDark, colors } = useDashboardTheme();
  const [formData, setFormData] = useState({
    name: "",
    managerIds: [] as string[],
    candidateIds: [] as string[],
  });

  const [errors, setErrors] = useState({ name: false, managerIds: false, candidateIds: false });
  const [userOptions, setUserOptions] = useState<
    { label: string; value: string; role: string }[]
  >([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: addTeam, isPending: isAddingPending } = useAddTeam();
  const { mutate: updateTeam, isPending: isUpdatingPending } = useUpdateTeam();
  const { mutate: deleteWholeTeam, isPending } = usedeleteWholeTeam();
  const tenantId =
    editUserData?.tenantId || localStorage.getItem("tenantId") || "";
  const { refetch } = useTeams(tenantId);
  const { role } = useAuthStore();
  const { data: userData } = useUsers({ currentPage: 1, pageSize: 100, currentAuthRole: role });

  useEffect(() => {
    if (editUserData) {
      const managerIds =
        editUserData.members
          ?.filter((m) => m.role === "MANAGER" || m.role === "ADMIN")
          .map((m) => m.id) || [];
      const candidateIds =
        editUserData.members
          ?.filter((m) => m.role === "USER" || m.role === "REP")
          .map((m) => m.id) || [];
      setFormData({ name: editUserData.name || "", managerIds, candidateIds });
    } else {
      setFormData({ name: "", managerIds: [], candidateIds: [] });
    }
  }, [editUserData]);

  useEffect(() => {
    if (userData?.data?.items) {
      const mapped = userData.data.items.map(
        ({ id, name, role }: { id: string; name: string; role: string }) => ({
          label: name,
          value: id,
          role,
        }),
      );
      setUserOptions(mapped);
    }
  }, [userData]);

  const validateForm = () => {
    const newErrors = { name: false, managerIds: false, candidateIds: false };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = true;
      isValid = false;
    }

    if (formData.managerIds.length < 1) {
      newErrors.managerIds = true;
      isValid = false;
    }

    if (formData.candidateIds.length < 1) {
      newErrors.candidateIds = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const handleDelete = () => {
    if (editUserData?.id) {
      deleteWholeTeam(editUserData.id, {
        onSuccess: () => {
          toast.success("Team Removed Successfully");
          refetch();
          queryClient.invalidateQueries({ queryKey: ["users"] });
          setShowDeleteDialog(false);
          onClose();
        },
        onError: (err: Error) => {
          let message = "Failed to remove team.";
          if ((err as AxiosError).isAxiosError) {
            const axiosError = err as AxiosError<{ message?: string }>;
            message = axiosError.response?.data?.message || message;
          }
          console.error("Delete team error:", err);
          toast.error(message);
          setShowDeleteDialog(false);
        },
      });
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;

    if (editUserData?.id) {
      const payload = {
        tenantId: editUserData.id,
        name: formData.name,
        userIds: [...formData.managerIds, ...formData.candidateIds],
      };

      updateTeam(payload, {
        onSuccess: () => {
          toast.success("Team Updated");
          queryClient.invalidateQueries({ queryKey: ["users"] });
          onClose();
          refetch();
        },
        onError: (err: Error) => {
          let message = "Failed to update team.";
          if ((err as AxiosError).isAxiosError) {
            const axiosError = err as AxiosError<{ message?: string }>;
            message = axiosError.response?.data?.message || message;
          }
          console.error("Update team error:", err);
          toast.error(message);
        },
      });
    } else {
      const payload = {
        name: formData.name,
        userIds: [...formData.managerIds, ...formData.candidateIds],
        tenantId,
      };

      addTeam(payload, {
        onSuccess: () => {
          toast.success("Team Created");
          queryClient.invalidateQueries({ queryKey: ["users"] });
          onClose();
          refetch();
        },
        onError: (err: Error) => {
          let message = "Failed to create team.";
          if ((err as AxiosError).isAxiosError) {
            const axiosError = err as AxiosError<{ message?: string }>;
            message = axiosError.response?.data?.message || message;
          }
          console.error("Create team error:", err);
          toast.error(message);
        },
      });
    }
  };
  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute" as const,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 600,
              maxWidth: "92vw",
              backgroundColor: isDark ? "#1C1A26" : "#FFFFFF",
              border: `1px solid ${colors.border}`,
              boxShadow: 24,
              borderRadius: "20px",
              p: 4,
              color: colors.text,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: colors.text,
                backgroundColor: colors.surfaceAlt,
                "&:hover": { backgroundColor: colors.border },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" mb={2} fontWeight="bold" style={{ color: colors.text }}>
              {editUserData ? "Edit Team" : "Create Team"}
            </Typography>

            <TextField
              fullWidth
              label="Team Name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setErrors((prev) => ({ ...prev, name: false }));
              }}
              error={errors.name}
              helperText={errors.name && "Team name is required"}
              sx={{
                mb: 3,
                "& .MuiInputLabel-root": { color: colors.textMuted },
                "& .MuiOutlinedInput-root": {
                  color: colors.text,
                  backgroundColor: colors.inputBg,
                  "& fieldset": { borderColor: colors.border },
                  "&:hover fieldset": { borderColor: colors.border },
                  "&.Mui-focused fieldset": { borderColor: isDark ? "#B8F890" : "#5a9d50" },
                },
                "& .MuiFormHelperText-root": { color: "#f44336" },
              }}
            />

            <Autocomplete
              multiple
              options={userOptions.filter((u) => u.role === "MANAGER" || u.role === "ADMIN")}
              getOptionLabel={(option) => option.label}
              value={userOptions.filter((u) =>
                formData.managerIds.includes(u.value),
              )}
              onChange={(_, newValue) => {
                const selectedIds = newValue.map((v) => v.value);
                setFormData({ ...formData, managerIds: selectedIds });
                setErrors((prev) => ({ ...prev, managerIds: false }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...restTagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      label={option.label}
                      {...restTagProps}
                      sx={{
                        color: colors.text,
                        borderColor: colors.border,
                        backgroundColor: colors.surfaceAlt,
                        "& .MuiChip-deleteIcon": { color: colors.textMuted },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Managers"
                  placeholder="Select Managers"
                  error={errors.managerIds}
                  helperText={
                    errors.managerIds && "At least one Manager is required"
                  }
                  sx={{
                    mb: 3,
                    "& .MuiInputLabel-root": { color: colors.textMuted },
                    "& .MuiOutlinedInput-root": {
                      color: colors.text,
                      backgroundColor: colors.inputBg,
                      "& fieldset": { borderColor: colors.border },
                      "&:hover fieldset": { borderColor: colors.border },
                      "&.Mui-focused fieldset": { borderColor: isDark ? "#B8F890" : "#5a9d50" },
                    },
                    "& .MuiFormHelperText-root": { color: "#f44336" },
                  }}
                />
              )}
            />

            <Autocomplete
              multiple
              options={userOptions.filter((u) => u.role === "USER" || u.role === "REP")}
              getOptionLabel={(option) => option.label}
              value={userOptions.filter((u) =>
                formData.candidateIds.includes(u.value),
              )}
              onChange={(_, newValue) => {
                const selectedIds = newValue.map((v) => v.value);
                setFormData({ ...formData, candidateIds: selectedIds });
                setErrors((prev) => ({ ...prev, candidateIds: false }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...restTagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      label={option.label}
                      {...restTagProps}
                      sx={{
                        color: colors.text,
                        borderColor: colors.border,
                        backgroundColor: colors.surfaceAlt,
                        "& .MuiChip-deleteIcon": { color: colors.textMuted },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Reps"
                  placeholder="Select Reps"
                  error={errors.candidateIds}
                  helperText={
                    errors.candidateIds && "At least one Rep is required"
                  }
                  sx={{
                    mb: 3,
                    "& .MuiInputLabel-root": { color: colors.textMuted },
                    "& .MuiOutlinedInput-root": {
                      color: colors.text,
                      backgroundColor: colors.inputBg,
                      "& fieldset": { borderColor: colors.border },
                      "&:hover fieldset": { borderColor: colors.border },
                      "&.Mui-focused fieldset": { borderColor: isDark ? "#B8F890" : "#5a9d50" },
                    },
                    "& .MuiFormHelperText-root": { color: "#f44336" },
                  }}
                />
              )}
            />

            <Box display="flex" justifyContent={editUserData ? "space-between" : "flex-end"} gap={2}>
              {editUserData && (
                <Button
                  onClick={() => setShowDeleteDialog(true)}
                  variant="outlined"
                  color="error"
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    fontWeight: "bold",
                  }}
                >
                  {isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Remove Team"
                  )}
                </Button>
              )}

              <Button
                onClick={handleSave}
                sx={{
                  width: "fit-content",
                  backgroundColor: isDark ? "#B8F890" : "#5a9d50",
                  color: isDark ? "#000" : "#fff",
                  fontWeight: "bold",
                  borderRadius: 999,
                  px: 4,
                  py: 1.2,
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": { backgroundColor: isDark ? "#a0e070" : "#4c8a43" },
                }}
              >
                {editUserData ? (
                  isUpdatingPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Update"
                  )
                ) : isAddingPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Create"
                )}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        PaperProps={{ sx: { backgroundColor: isDark ? "#1C1A26" : "#FFFFFF", color: colors.text, border: `1px solid ${colors.border}`, borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: colors.text }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3, color: colors.textMuted }}>
            Are you sure you want to delete this Team?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelDelete}
                  sx={{
                    color: colors.text,
                    borderColor: colors.border,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: "bold",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  sx={{
                    fontWeight: "bold",
                    borderRadius: "10px",
                    textTransform: "none",
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Add_Team;
